import { DATE_FORMAT_RANGE_FNS, GetRelationTrackingPointsAggregateResponse } from '@schooly/api';
import { format, isAfter } from 'date-fns';

import { formatWeekRangeLabel, hasTrackingPointEntry, parseDate } from './buildGroupGrid';
import { isDateWithinValidity, ValidityInterval } from './studentValidity';
import { buildTrackingPoints, cellBelongsToPoint, trackingPointKey } from './trackingPoints';
import {
  AttendanceGridCell,
  AttendanceGridCellValue,
  AttendanceGridColumn,
  AttendanceGridStudent,
  AttendanceGridTrackingPoint,
  AttendanceGridWeekRow,
} from './types';

type Week = GetRelationTrackingPointsAggregateResponse['weeks'][number];
type WeekRow = Week['rows'][number];
type WeekCell = WeekRow['cells'][number];

type TrackingPointColumn = AttendanceGridColumn & { trackingPoint: AttendanceGridTrackingPoint };

const toCellMeta = (cell: WeekCell) => ({
  period_id: cell.period_id,
  sessions: cell.session ? [cell.session] : [],
});

const findCellForPoint = (
  cells: WeekCell[],
  point: AttendanceGridTrackingPoint,
): WeekCell | undefined => cells.find((cell) => cellBelongsToPoint(toCellMeta(cell), point));

const columnKey = (week: Week, trackingPoint: AttendanceGridTrackingPoint): string =>
  `${week.start}:${trackingPointKey(trackingPoint.session, trackingPoint.period?.id ?? null)}`;

const buildColumns = (week: Week): TrackingPointColumn[] =>
  buildTrackingPoints(week.tracking_points).map((trackingPoint) => ({
    key: columnKey(week, trackingPoint),
    date: null,
    label: trackingPoint.label,
    sessions: [],
    trackingPoint,
    has_register: true,
  }));

const rowNotice = (
  row: WeekRow,
  trackingPoint: AttendanceGridTrackingPoint,
): Pick<AttendanceGridCellValue, 'justification' | 'absence_request'> =>
  trackingPoint.session
    ? { justification: row.justification, absence_request: row.absence_request }
    : { justification: null, absence_request: null };

const toCellValue = (
  row: WeekRow,
  trackingPoint: AttendanceGridTrackingPoint,
  cell: WeekCell,
): AttendanceGridCellValue => {
  const periodId = trackingPoint.period?.id;

  if (periodId) {
    return { ...rowNotice(row, trackingPoint), am: null, pm: null, periods: { [periodId]: cell } };
  }

  return {
    ...rowNotice(row, trackingPoint),
    am: trackingPoint.session === 'am' ? cell : null,
    pm: trackingPoint.session === 'pm' ? cell : null,
  };
};

type BuildCellInput = {
  column: TrackingPointColumn;
  row: WeekRow;
  relationId: string;
  isFuture: boolean;
};

const buildCell = ({ column, row, relationId, isFuture }: BuildCellInput): AttendanceGridCell => {
  const cell = findCellForPoint(row.cells, column.trackingPoint);

  if (!cell?.register) {
    return { column_key: column.key, kind: 'blocked', editable: false };
  }

  const value = toCellValue(row, column.trackingPoint, cell);

  return {
    column_key: column.key,
    kind: 'normal',
    editable: !isFuture && hasTrackingPointEntry(value),
    data: {
      register_id: cell.register.id,
      date: cell.register.date,
      relation_id: relationId,
      cell: value,
    },
  };
};

export type TrackingPointsWeekGrid = {
  weekLabel: string;
  columns: AttendanceGridColumn[];
  rows: AttendanceGridWeekRow[];
};

export type BuildStudentTrackingPointsWeekGridInput = {
  week: Week;
  student: AttendanceGridStudent;
  validity: ValidityInterval[];
  today: Date;
};

export const buildStudentTrackingPointsWeekGrid = ({
  week,
  student,
  validity,
  today,
}: BuildStudentTrackingPointsWeekGridInput): TrackingPointsWeekGrid => {
  const columns = buildColumns(week);
  const hasValidity = validity.length > 0;

  const rows: AttendanceGridWeekRow[] = [...week.rows]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((row) => {
      const date = parseDate(row.date);
      const outOfValidity = hasValidity && !isDateWithinValidity(date, validity);
      const isFuture = isAfter(date, today);

      const cells = columns.map((column) =>
        outOfValidity
          ? { column_key: column.key, kind: 'out_of_day' as const, editable: false }
          : buildCell({ column, row, relationId: student.relation_id, isFuture }),
      );

      return {
        key: row.date,
        label: format(date, DATE_FORMAT_RANGE_FNS),
        student,
        cells,
      };
    });

  return { weekLabel: formatWeekRangeLabel(week.start, week.end), columns, rows };
};
